import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Loader, ArrowLeft, Printer, FileText, Upload, CheckCircle, X } from "lucide-react";
import { convertLatexToMarkup } from "mathlive";
import {
  lmsGetManualPaperById, lmsGetAnswerSheets, lmsUploadAnswerSheet,
} from "../api/lmsApi";
import "../lms.css";

const getStudentData = () => {
  try { return JSON.parse(localStorage.getItem("userData") || "{}"); }
  catch { return {}; }
};

/* ── same helpers as ManualPaperBuilder ── */
function parseMatchPairs(text) {
  if (!text) return [];
  try {
    const parsed = JSON.parse(text);
    // New format: {instruction, colA, colB, answers}
    if (Array.isArray(parsed.colA)) {
      return parsed.colA.map((a, i) => {
        const ansLetter = parsed.answers?.[i];
        const bIdx = ansLetter ? ansLetter.charCodeAt(0) - 97 : i;
        return { a, b: parsed.colB?.[bIdx] ?? parsed.colB?.[i] ?? "" };
      });
    }
    // Legacy format: {pairs:[{a,b}]}
    if (parsed.pairs && parsed.pairs.length) return parsed.pairs;
  } catch {}
  const m = text.match(/^(.*?)\s*B\s*:\s*(.+)$/is);
  if (!m) return [{ a: text, b: "" }];
  const colA = m[1].replace(/^A\s*:\s*/i, "").trim().split(/[,،;]\s*/).filter(Boolean);
  const colB = m[2].trim().split(/[,،;]\s*/).filter(Boolean);
  const len = Math.max(colA.length, colB.length, 1);
  return Array.from({ length: len }, (_, i) => ({ a: colA[i] || "", b: colB[i] || "" }));
}

function parseMatchInstruction(text) {
  if (!text) return "";
  try {
    const parsed = JSON.parse(text);
    return parsed.instruction || "";
  } catch {}
  return "";
}

function MathPreview({ latex }) {
  if (!latex) return null;
  const hasLatex = /[\\^_{}]/.test(latex);
  if (!hasLatex) return <span>{latex}</span>;
  try {
    const html = convertLatexToMarkup(latex, { mathstyle: "textstyle" });
    return <span dangerouslySetInnerHTML={{ __html: html }} />;
  } catch {
    return <span>{latex}</span>;
  }
}

/* ── Answer Upload Panel ── */
function AnswerUploadPanel({ exam, schoolId, studentId }) {
  const [answerSheet,  setAnswerSheet]  = useState(null);
  const [file,         setFile]         = useState(null);
  const [uploading,    setUploading]    = useState(false);
  const [loadingAns,   setLoadingAns]   = useState(true);
  const fileRef = useRef();

  const loadAnswer = () => {
    if (!studentId || !exam?.id) { setLoadingAns(false); return; }
    lmsGetAnswerSheets({ exam_id: exam.id, student_id: studentId })
      .then((res) => setAnswerSheet((res?.data?.data || [])[0] || null))
      .catch(() => {})
      .finally(() => setLoadingAns(false));
  };

  useEffect(() => { loadAnswer(); }, [exam?.id, studentId]);

  const handleUpload = async () => {
    if (!file) return toast.error("Please select a file");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file",       file);
      fd.append("school_id",  schoolId);
      fd.append("exam_id",    exam.id);
      fd.append("student_id", studentId);
      await lmsUploadAnswerSheet(fd);
      toast.success("Answer sheet submitted!");
      setFile(null);
      loadAnswer();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Upload failed");
    } finally { setUploading(false); }
  };

  if (loadingAns) return null;

  return (
    <div className="no-print" style={{
      background: "#fff", borderRadius: 14, border: "1.5px solid #e0e7ff",
      boxShadow: "0 2px 8px rgba(0,0,0,0.05)", overflow: "hidden", marginTop: 20,
    }}>
      <div style={{ padding: "12px 20px", background: "linear-gradient(90deg,#4f46e5,#7c3aed)",
        display: "flex", alignItems: "center", gap: 8 }}>
        <Upload size={15} color="#fff" />
        <span style={{ fontWeight: 700, fontSize: 14, color: "#fff" }}>Submit Your Answer Sheet</span>
      </div>
      <div style={{ padding: "20px" }}>
        {answerSheet && (
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px",
            background: "#f0fdf4", border: "1.5px solid #86efac", borderRadius: 10, marginBottom: 16 }}>
            <CheckCircle size={22} color="#16a34a" />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#15803d" }}>Answer Sheet Submitted</div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>{answerSheet.file_name}</div>
              {answerSheet.marks_obtained != null && (
                <div style={{ fontSize: 13, fontWeight: 700, color: "#15803d", marginTop: 4 }}>
                  Score: {answerSheet.marks_obtained} / {exam?.total_marks || "—"}
                  {answerSheet.grade && ` · Grade: ${answerSheet.grade}`}
                </div>
              )}
              {answerSheet.teacher_remarks && (
                <div style={{ fontSize: 12, color: "#374151", marginTop: 6,
                  padding: "6px 10px", background: "#fffbeb", borderRadius: 6, border: "1px solid #fde68a" }}>
                  <strong>Teacher Remarks:</strong> {answerSheet.teacher_remarks}
                </div>
              )}
            </div>
            <a href={answerSheet.file_url} target="_blank" rel="noreferrer" style={{
              fontSize: 12, fontWeight: 700, color: "#4f46e5", textDecoration: "none",
              padding: "6px 14px", border: "1.5px solid #e0e7ff", borderRadius: 7, background: "#fff",
            }}>View</a>
          </div>
        )}

        <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 14px" }}>
          {answerSheet
            ? "Re-upload to replace your previous submission."
            : "Print this paper, write your answers, then upload as PDF or photo."}
        </p>

        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png"
          style={{ display: "none" }} onChange={(e) => setFile(e.target.files[0] || null)} />

        {file && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
            background: "#f3f4f6", borderRadius: 8, marginBottom: 12 }}>
            <FileText size={16} color="#4f46e5" />
            <span style={{ fontSize: 13, flex: 1, color: "#374151" }}>{file.name}</span>
            <button onClick={() => setFile(null)} style={{ border: "none", background: "transparent",
              cursor: "pointer", color: "#9ca3af", fontSize: 18, lineHeight: 1 }}>×</button>
          </div>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => fileRef.current?.click()} style={{
            flex: 1, padding: "11px 0", borderRadius: 9, border: "2px dashed #a5b4fc",
            background: "#f5f3ff", color: "#4f46e5", fontSize: 13, fontWeight: 700, cursor: "pointer",
          }}>📎 {file ? "Change File" : "Choose File (PDF / Photo)"}</button>
          {file && (
            <button onClick={handleUpload} disabled={uploading} style={{
              flex: 1, padding: "11px 0", borderRadius: 9, border: "none",
              background: uploading ? "#a5b4fc" : "linear-gradient(135deg,#4f46e5,#7c3aed)",
              color: "#fff", fontSize: 13, fontWeight: 700, cursor: uploading ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}>
              {uploading ? <Loader size={14} className="ms2-spin" /> : <Upload size={14} />}
              {uploading ? "Submitting..." : "Submit Answer"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Main Page ── */
export default function StudentManualPaperViewer() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const userData  = getStudentData();
  const schoolId  = userData?.school_id || userData?.admin_id || null;
  const studentId = userData?.admin_id || null;

  const [paper,   setPaper]   = useState(null);
  const [loading, setLoading] = useState(true);
  const paperRef = useRef();

  useEffect(() => {
    lmsGetManualPaperById(id)
      .then((res) => setPaper(res.data))
      .catch(() => toast.error("Failed to load paper"))
      .finally(() => setLoading(false));
  }, [id]);

  const handlePrint = () => {
    const win = window.open("", "_blank");
    win.document.write(`
      <html><head><title>${paper?.title || "Question Paper"}</title>
      <link id="ml-css" rel="stylesheet" href="https://unpkg.com/mathlive/mathlive-static.css" />
      <link rel="stylesheet" href="https://unpkg.com/mathlive/mathlive-fonts.css" />
      <style>
        body { font-family: Arial, sans-serif; margin: 32px; color: #111; }
        h1 { font-size: 20px; text-align: center; margin: 0; }
        .meta { text-align: center; font-size: 13px; color: #444; margin: 6px 0 18px; }
        .divider { border-top: 2px solid #111; margin: 10px 0; }
        .instructions { font-size: 12px; background: #fff9db; padding: 8px 12px; border-left: 3px solid #fbbf24; margin-bottom: 16px; }
        .section-title { font-size: 14px; font-weight: bold; margin: 18px 0 6px; border-bottom: 1px solid #ddd; padding-bottom: 4px; }
        .section-meta { font-size: 11px; color: #666; margin-bottom: 10px; }
        .question { margin-bottom: 14px; }
        .q-text { font-size: 13px; margin-bottom: 6px; }
        .options { display: grid; grid-template-columns: 1fr 1fr; gap: 2px 20px; font-size: 12px; margin-left: 20px; }
        .blank { display: inline-block; width: 80px; }
        @media print { body { margin: 16px; } }
      </style></head><body>
      ${paperRef.current?.innerHTML || ""}
      </body></html>
    `);
    win.document.close();
    win.focus();
    win.onload = () => {
      const css = win.document.getElementById("ml-css");
      if (css && !css.sheet) { css.onload = () => win.print(); } else { win.print(); }
    };
  };

  if (loading) return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
      <Loader size={28} className="ms2-spin" color="#4f46e5" />
    </div>
  );
  if (!paper) return (
    <div style={{ textAlign: "center", padding: 60, color: "#9ca3af" }}>Paper not found.</div>
  );

  const totalMarks = paper.sections?.reduce((s, sec) =>
    s + (sec.questions?.reduce((sq, q) => sq + parseFloat(q.marks || sec.marks_per_q || 0), 0) || 0), 0) || 0;

  return (
    <div className="lms-content-inner">

      {/* Toolbar */}
      <div className="no-print" style={{
        background: "#fff", borderRadius: 14, marginBottom: 20,
        border: "1px solid #e5e7eb", boxShadow: "0 2px 8px rgba(0,0,0,0.05)", overflow: "hidden",
      }}>
        <div style={{ height: 4, background: "linear-gradient(90deg,#4f46e5,#7c3aed,#0891b2)" }} />
        <div style={{ padding: "14px 20px", display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => navigate(-1)} style={{
            display: "flex", alignItems: "center", gap: 5, fontSize: 13, fontWeight: 600,
            color: "#6b7280", background: "#f3f4f6", border: "none", borderRadius: 8,
            padding: "7px 12px", cursor: "pointer",
          }}>
            <ArrowLeft size={14} /> Back
          </button>
          <div style={{ flex: 1, fontWeight: 700, fontSize: 16, color: "#111827" }}>{paper.title}</div>
          <button onClick={handlePrint} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "9px 18px",
            borderRadius: 8, border: "none",
            background: "linear-gradient(135deg,#4f46e5,#7c3aed)",
            color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
          }}>
            <Printer size={14} /> Print / Save PDF
          </button>
        </div>
      </div>

      {/* Paper — same layout as admin PrintPreview */}
      <div style={{ background: "#f8fafc", borderRadius: 14, padding: "28px 32px", marginBottom: 0 }}>
        <div ref={paperRef} style={{
          background: "#fff", padding: "32px 36px",
          boxShadow: "0 2px 16px rgba(0,0,0,0.08)", borderRadius: 8,
          maxWidth: 720, margin: "0 auto",
        }}>
          {/* Header */}
          {paper.header_text && (
            <div style={{ textAlign: "center", fontSize: 13, color: "#555", marginBottom: 6 }}>
              {paper.header_text}
            </div>
          )}
          <h1 style={{ fontSize: 20, textAlign: "center", margin: "0 0 6px", fontWeight: 800 }}>
            {paper.title}
          </h1>
          <div style={{ textAlign: "center", fontSize: 13, color: "#555", marginBottom: 4 }}>
            {[paper.class_name, paper.subject_name].filter(Boolean).join(" · ")}
            {paper.paper_set ? ` | Set: ${paper.paper_set}` : ""}
          </div>
          <div style={{ textAlign: "center", fontSize: 13, color: "#555", marginBottom: 10 }}>
            {paper.duration_minutes ? `Time: ${paper.duration_minutes} min` : ""}
            {paper.duration_minutes && totalMarks ? " | " : ""}
            {totalMarks ? `Total Marks: ${totalMarks}` : ""}
          </div>
          <div style={{ borderTop: "2px solid #111", borderBottom: "2px solid #111", margin: "10px 0", padding: "1px 0" }} />

          {/* Instructions */}
          {paper.instructions && (
            <div style={{ fontSize: 12, background: "#fffbeb", padding: "8px 12px",
              borderLeft: "3px solid #fbbf24", margin: "12px 0", lineHeight: 1.7 }}>
              <strong>Instructions:</strong> {paper.instructions}
            </div>
          )}

          {/* Sections */}
          {paper.sections?.map((sec, si) => (
            <div key={sec.id}>
              <div style={{ fontWeight: 800, fontSize: 14, marginTop: 20, marginBottom: 4 }}>
                SECTION {sec.section_label}{sec.title ? ` — ${sec.title}` : ""}
                <span style={{ float: "right", fontWeight: 600, fontSize: 13 }}>
                  [{sec.marks_per_q} mark{sec.marks_per_q !== 1 ? "s" : ""} each]
                </span>
              </div>
              {sec.instructions && (
                <div style={{ fontSize: 11, color: "#555", marginBottom: 8 }}>{sec.instructions}</div>
              )}
              <div style={{ borderBottom: "1px solid #ddd", marginBottom: 10 }} />

              {sec.questions?.map((q, qi) => {
                const renderFillBlank = (text) => {
                  const parts = text.split(/(_+)/g);
                  return parts.map((part, i) =>
                    /^_+$/.test(part)
                      ? <span key={i} style={{ display: "inline-block", minWidth: 80,
                          borderBottom: "1.5px solid #111", marginLeft: 2, marginRight: 2 }}>&nbsp;</span>
                      : part
                  );
                };

                const renderMatchFollowing = (text) => {
                  const pairs = parseMatchPairs(text);
                  return (
                    <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8, fontSize: 12 }}>
                      <thead>
                        <tr>
                          {["No.", "Column A", "Match", "Column B"].map((h) => (
                            <th key={h} style={{ padding: "4px 8px", border: "1px solid #ccc",
                              background: "#f3f4f6", fontWeight: 700, fontSize: 11 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {pairs.map((pair, i) => (
                          <tr key={i}>
                            <td style={{ padding: "5px 6px", border: "1px solid #ddd", textAlign: "center", color: "#555" }}>{i + 1}.</td>
                            <td style={{ padding: "5px 8px", border: "1px solid #ddd" }}><MathPreview latex={pair.a || ""} /></td>
                            <td style={{ padding: "5px 6px", border: "1px solid #ddd", textAlign: "center" }}>
                              <span style={{ display: "inline-block", width: 36, borderBottom: "1px solid #111" }}>&nbsp;</span>
                            </td>
                            <td style={{ padding: "5px 8px", border: "1px solid #ddd" }}><MathPreview latex={pair.b || ""} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                };

                return (
                  <div key={q.id} style={{ marginBottom: 18 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.7 }}>
                      Q{q.question_no || qi + 1}.{" "}
                      {sec.question_type === "fill_blank"
                        ? renderFillBlank(q.question_text)
                        : sec.question_type === "match_following"
                          ? <span>{parseMatchInstruction(q.question_text) || "Match the following:"}</span>
                          : <MathPreview latex={q.question_text} />}
                      {q.marks && parseFloat(q.marks) !== parseFloat(sec.marks_per_q) && (
                        <span style={{ float: "right", fontSize: 12, fontWeight: 400, color: "#555" }}>
                          [{q.marks} marks]
                        </span>
                      )}
                    </div>

                    {/* MCQ / True-False options */}
                    {q.options?.length > 0 && sec.question_type !== "match_following" && (
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 24px",
                        fontSize: 12, marginTop: 6, marginLeft: 20 }}>
                        {q.options.map((opt) => (
                          <div key={opt.id} style={{ display: "flex", gap: 4, alignItems: "center" }}>
                            <span>({opt.option_label})</span>
                            <MathPreview latex={opt.option_text || "___"} />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Match the Following */}
                    {sec.question_type === "match_following" && (
                      <div style={{ marginLeft: 20, marginTop: 4 }}>
                        {renderMatchFollowing(q.question_text)}
                      </div>
                    )}

                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Answer Upload — ONLY show for offline exams */}
      {paper.exam_mode === "offline" && (
        <AnswerUploadPanel
          exam={{ id: paper.exam_id, total_marks: totalMarks }}
          schoolId={schoolId}
          studentId={studentId}
        />
      )}

      {/* If ONLINE exam, show portal banner instead of answer sheet upload */}
      {paper.exam_mode === "online" && paper.exam_id && (
        <div className="no-print" style={{
          marginTop: 20, padding: "16px 20px", borderRadius: 12,
          background: "#f0fdf4", border: "1.5px solid #bbf7d0",
          display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12,
        }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#166534" }}>This is an Online Examination</div>
            <div style={{ fontSize: 12, color: "#15803d", marginTop: 2 }}>
              Answers are submitted online via the examination portal. File upload is not required for online exams.
            </div>
          </div>
          <button
            onClick={() => navigate(`/student-lms/exams/${paper.exam_id}/online`)}
            style={{
              padding: "9px 20px", borderRadius: 8, border: "none",
              background: "linear-gradient(135deg,#16a34a,#15803d)",
              color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer",
            }}
          >
            Go to Online Exam &rarr;
          </button>
        </div>
      )}
    </div>
  );
}
